import type {
  AssetGenJob,
  BuildPhase,
  CreationVersion,
  GameDesign,
  ReferenceBrief,
  VideoBrief,
} from '@djgg/creations';
import { formatApprovedMusicDirection } from '@djgg/creations';

export const CREATION_CHAPTERS = [
  'Your idea',
  'Meet your game',
  'Deej’s making it',
  'Final checks',
  'Let’s play',
] as const;
export type CreationChapter = (typeof CREATION_CHAPTERS)[number];

export function chapterForPhase(phase: BuildPhase): CreationChapter {
  if (phase === 'live') return 'Let’s play';
  if (phase === 'audit' || phase === 'publishing') return 'Final checks';
  return 'Deej’s making it';
}

export type DiscoveryKind = 'inspiration' | 'idea' | 'vocabulary';
export type DiscoveryState = 'preparing' | 'ready' | 'unavailable';
export interface AnalysisDiscovery {
  id: string;
  kind: DiscoveryKind;
  state: DiscoveryState;
  title: string;
  summary: string;
  thumbnailUrl?: string;
}

function referenceHost(url: string): string {
  try { return new URL(url).hostname; }
  catch { return 'Reference'; }
}

/** Creator-visible discoveries only. Pending references are labelled as such;
 * design ideas do not appear until analysis actually returned them. */
export function analysisDiscoveries(input: {
  concept: string;
  referenceUrls: readonly string[];
  referenceBriefs?: readonly ReferenceBrief[];
  videoBriefs?: readonly VideoBrief[];
  design?: GameDesign | null;
}): AnalysisDiscovery[] {
  const briefs = new Map((input.referenceBriefs ?? []).map((brief) => [brief.url, brief]));
  const discoveries: AnalysisDiscovery[] = input.referenceUrls.filter(Boolean).map((url) => {
    const brief = briefs.get(url);
    return {
      id: `reference:${url}`,
      kind: 'inspiration' as const,
      state: brief?.status === 'ok' ? 'ready' as const : brief?.status === 'failed' ? 'unavailable' as const : 'preparing' as const,
      title: brief?.title || referenceHost(url),
      summary: brief?.status === 'ok'
        ? brief.summary
        : brief?.status === 'failed'
          ? 'This reference could not be summarized. Your game can continue without it.'
          : 'Waiting for a real reference summary.',
    };
  });
  for (const video of input.videoBriefs ?? []) {
    discoveries.push({
      id: `video:${video.videoId}`,
      kind: 'inspiration',
      state: video.status === 'ok' ? 'ready' : 'unavailable',
      title: video.title || video.game,
      summary: video.observations?.summary ?? 'The gameplay preview was unavailable, so it was not used.',
      ...(video.status === 'ok' ? { thumbnailUrl: `https://i.ytimg.com/vi/${encodeURIComponent(video.videoId)}/mqdefault.jpg` } : {}),
    });
  }
  if (input.design) {
    const design = input.design;
    discoveries.push(
      { id: 'design:play', kind: 'idea', state: 'ready', title: 'How you play', summary: design.summary },
      { id: 'design:goal', kind: 'idea', state: 'ready', title: 'Your challenge', summary: design.winCondition },
    );
    if (design.artDirection?.style || design.theme) discoveries.push({
      id: 'design:world', kind: 'idea', state: 'ready', title: 'The world taking shape',
      summary: design.artDirection?.style || design.theme || '',
    });
  }
  return discoveries;
}

export interface MusicDirection {
  mood: string;
  genre: string;
  tempoBpm: number;
  instrumentation: string;
}

/** Deterministic, editable recommendation from the approved design. This is
 * never sent anywhere by itself; Build approval records the resulting bounded
 * line in the creator-owned design. */
export function recommendedMusicDirection(design: GameDesign): MusicDirection {
  const text = `${design.summary} ${design.theme ?? ''} ${design.artDirection?.style ?? ''}`.toLowerCase();
  if (/space|star|moon|planet|sci-fi/.test(text)) return { mood: 'adventurous and spacious', genre: 'retro electronic', tempoBpm: 118, instrumentation: 'warm synth, pulse bass, electronic drums' };
  if (/race|battle|fight|chase|shooter|action/.test(text)) return { mood: 'energetic and focused', genre: 'arcade electronic', tempoBpm: 142, instrumentation: 'synth lead, bass, punchy drums' };
  if (/cozy|calm|garden|puzzle|gentle/.test(text)) return { mood: 'playful and relaxed', genre: 'light game soundtrack', tempoBpm: 96, instrumentation: 'marimba, soft synth, brushed drums' };
  return { mood: 'playful and adventurous', genre: 'modern arcade', tempoBpm: 120, instrumentation: 'synth lead, bass, electronic drums' };
}

export function musicDirectionLine(direction: MusicDirection): string {
  return formatApprovedMusicDirection({
    mood: direction.mood.trim().slice(0, 80) || 'playful',
    genre: direction.genre.trim().slice(0, 80) || 'game soundtrack',
    tempoBpm: Math.min(200, Math.max(40, Math.round(direction.tempoBpm))),
    instrumentation: direction.instrumentation.split(',').map((item) => item.trim().slice(0, 80)).filter(Boolean).slice(0, 6).length
      ? direction.instrumentation.split(',').map((item) => item.trim().slice(0, 80)).filter(Boolean).slice(0, 6)
      : ['synth'],
  });
}

export function parseMusicDirection(value: string | undefined, fallback: MusicDirection): MusicDirection {
  if (!value) return fallback;
  const parts = Object.fromEntries(value.split(/\s*[;·|]\s*/).map((part) => {
    const [key, ...rest] = part.split(':');
    return [key?.trim().toLowerCase(), rest.join(':').trim()];
  }));
  const tempo = /\d{2,3}/.exec(parts.tempo ?? '')?.[0];
  return {
    mood: parts.mood || value.slice(0, 80),
    genre: parts.genre || fallback.genre,
    tempoBpm: tempo ? Math.min(200, Math.max(40, Number(tempo))) : fallback.tempoBpm,
    instrumentation: parts.instruments || fallback.instrumentation,
  };
}

function median(values: number[]): number | null {
  if (values.length < 3) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle]! : Math.round((sorted[middle - 1]! + sorted[middle]!) / 2);
}

/** Production rows can contain legacy JSON timestamp sentinels. Never allow a
 * malformed value to participate in arithmetic or reach a timer formatter. */
function finiteMilliseconds(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export interface JourneyTimingView {
  totalElapsedMs: number | null;
  queueMs: number | null;
  buildDurationMs: number | null;
  typicalBuildDurationMs: number | null;
  estimatedPercent: number | null;
  overrun: boolean;
}

export function journeyTiming(
  version: Pick<CreationVersion, 'buildStatus' | 'buildTiming' | 'buildMode'> & Partial<Pick<CreationVersion, 'n'>>,
  history: ReadonlyArray<Pick<CreationVersion, 'buildStatus' | 'buildTiming' | 'buildMode'> & Partial<Pick<CreationVersion, 'n'>>>,
  now: number,
  observedTypicalBuildDurationMs?: number,
): JourneyTimingView {
  const timing = version.buildTiming;
  if (!timing) return { totalElapsedMs: null, queueMs: null, buildDurationMs: null, typicalBuildDurationMs: null, estimatedPercent: null, overrun: false };
  const comparable = history.filter((item) => {
      const isCurrentRun = item === version || (version.n != null && item.n === version.n);
      return !isCurrentRun && item.buildStatus === 'live' && item.buildMode === version.buildMode;
    }).flatMap((item) => {
      const duration = finiteMilliseconds(item.buildTiming?.buildDurationMs);
      return duration == null ? [] : [duration];
    });
  const observedTypical = finiteMilliseconds(observedTypicalBuildDurationMs);
  const typicalBuildDurationMs = observedTypicalBuildDurationMs !== undefined
    ? observedTypical
    : median(comparable);
  const terminal = version.buildStatus === 'live' || version.buildStatus === 'failed';
  const acceptedAt = finiteMilliseconds(timing.acceptedAt);
  const workerStartedAt = finiteMilliseconds(timing.workerStartedAt);
  const safeNow = finiteMilliseconds(now);
  const storedTotal = finiteMilliseconds(timing.totalElapsedMs);
  const storedQueue = finiteMilliseconds(timing.queueMs);
  const storedBuild = finiteMilliseconds(timing.buildDurationMs);
  const totalElapsedMs = storedTotal ?? (terminal || acceptedAt == null || safeNow == null || acceptedAt > safeNow ? null : safeNow - acceptedAt);
  const openQueueMs = terminal || safeNow == null ? 0 : (Array.isArray(timing.queueIntervals) ? timing.queueIntervals : []).reduce(
    (sum, interval) => {
      if (finiteMilliseconds(interval.claimedAt) != null) return sum;
      const enteredAt = finiteMilliseconds(interval.enteredAt);
      return enteredAt == null || enteredAt > safeNow ? sum : sum + safeNow - enteredAt;
    },
    0,
  );
  const queueMs = workerStartedAt == null && totalElapsedMs != null
    ? totalElapsedMs
    : storedQueue == null ? null : storedQueue + openQueueMs;
  const buildDurationMs = storedBuild ?? (workerStartedAt == null || terminal || acceptedAt == null || safeNow == null || queueMs == null
    ? null
    : Math.max(0, safeNow - acceptedAt - queueMs));
  const estimatedPercent = version.buildStatus === 'live' ? 100
    : typicalBuildDurationMs == null || buildDurationMs == null ? null
      : Math.min(95, Math.max(2, Math.round((buildDurationMs / typicalBuildDurationMs) * 90)));
  return {
    totalElapsedMs,
    queueMs,
    buildDurationMs,
    typicalBuildDurationMs,
    estimatedPercent,
    overrun: typicalBuildDurationMs != null && buildDurationMs != null && buildDurationMs > typicalBuildDurationMs,
  };
}

export function mediaEmptyState(jobs: readonly AssetGenJob[], phase: BuildPhase, ready: boolean): string {
  const running = jobs.find((job) => job.status === 'running');
  if (running) return running.stage === 'postprocessing' ? 'Finishing the audio mix'
    : running.stage === 'inspecting' ? 'Checking the generated media'
      : running.stage === 'uploading' ? 'Saving the finished media' : 'Generating media';
  if (jobs.some((job) => job.status === 'awaiting-core')) return 'Media is waiting for core verification';
  if (jobs.some((job) => job.status === 'queued')) return 'Media is queued';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'configuration')) return 'Generated media is not configured for this run — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'timeout')) return 'Media reached its time limit after bounded retries — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'provider')) return 'The media provider could not finish after bounded retries — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'validation')) return 'Generated media did not pass final checks — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'worker')) return 'The media worker could not finish after bounded retries — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed' && job.failureKind === 'cancelled')) return 'Media stopped when this creation run ended; finished media remains available for repair';
  if (jobs.some((job) => job.status === 'failed' && job.attempts > 0)) return 'Retrying reached its limit — supported defaults will be used';
  if (jobs.some((job) => job.status === 'failed')) return 'Media could not finish — supported defaults will be used';
  if (ready) return 'This release uses supported default media; no generated candidates were included.';
  return phase === 'queued' || phase === 'setup' || phase === 'writing'
    ? 'Preparing the art and sound plan'
    : 'Waiting for the first finished media preview';
}
